import { authenticatedFetch } from '../JWTAuthService';
import { configuration } from '../Configuration';

/**
 * Layers the heatmap lays over its metric: power supply infrastructure and
 * earthquakes (`POST /map-layers/power-units`, `POST /map-layers/earthquakes`).
 *
 * Both come back drawn — elements with colours, shapes and markers already
 * decided — because the same server code answers the chat tools. Nothing here
 * classifies a power unit or picks a colour for a magnitude; that vocabulary
 * lives in the server's `powerInfrastructure` and `earthquakes` modules, and a
 * second copy of it in the portal would be a second answer to the same question.
 */

const baseUrl = configuration.serverUrl;

export interface PowerLayerResult {
    /** Every category the server knows, in its own order — the list to offer. */
    categories: string[];
    /** Drawn elements, keyed by category. Categories with nothing found are absent. */
    layers: Record<string, { name: string; elements: any[] }>;
    /** How many were *found* per category, which is more than was drawn when truncated. */
    counts: Record<string, number>;
    total: number;
    truncated: boolean;
}

export interface EarthquakeResult {
    elements: any[];
    /** Magnitude bands, strongest first — the legend. */
    scale: { min: number; color: string }[];
    strongest: number | null;
    count: number;
}

const post = async (path: string, body: any, whatFailed: string): Promise<any> => {
    const response = await authenticatedFetch(`${baseUrl}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });

    if (!response || !response.ok) {
        const detail = await response?.json().catch(() => null);
        throw new Error(detail?.error ?? `${whatFailed} (${response?.status ?? 'keine Antwort'})`);
    }

    return response.json();
};

export const loadPowerUnits = async (geometry: any): Promise<PowerLayerResult> => {
    const payload = await post(
        '/map-layers/power-units',
        { geometry },
        'Stromversorgungsinfrastruktur konnte nicht geladen werden',
    );

    return {
        categories: payload?.categories ?? [],
        layers: payload?.layers ?? {},
        counts: payload?.counts ?? {},
        total: payload?.total ?? 0,
        truncated: Boolean(payload?.truncated),
    };
};

export const loadEarthquakes = async (geometry: any, minMagnitude?: number): Promise<EarthquakeResult> => {
    const payload = await post(
        '/map-layers/earthquakes',
        minMagnitude === undefined ? { geometry } : { geometry, minMagnitude },
        'Erdbeben konnten nicht geladen werden',
    );

    return {
        elements: payload?.elements ?? [],
        scale: payload?.scale ?? [],
        strongest: payload?.strongest ?? null,
        count: payload?.count ?? 0,
    };
};

export interface WayLayerResult {
    categories: string[];
    layers: Record<string, { name: string; elements: any[] }>;
    counts: Record<string, number>;
    total: number;
    truncated: boolean;
}

/**
 * Roads and paths inside the area, per category.
 *
 * Only the ticked categories are asked for: footways outnumber through roads
 * several times over, and querying the lot to throw most of it away costs the
 * reader the wait for what they did want.
 */
export const loadWays = async (
    geometry: any,
    categories: string[],
    locale: string,
): Promise<WayLayerResult> => {
    const payload = await post(
        '/map-layers/ways',
        { geometry, categories, locale: locale.startsWith('de') ? 'de' : 'en' },
        'Straßen und Wege konnten nicht geladen werden',
    );

    return {
        categories: payload?.categories ?? [],
        layers: payload?.layers ?? {},
        counts: payload?.counts ?? {},
        total: payload?.total ?? 0,
        truncated: Boolean(payload?.truncated),
    };
};

/**
 * Bridges inside the area, per category.
 *
 * Every category comes back from one query: what tells them apart lives in three
 * different fields of three different imports, and a five-kilometre window holds
 * about thirty bridges — there is nothing to save by asking separately.
 */
export const loadBridges = async (geometry: any, locale: string): Promise<WayLayerResult> => {
    const payload = await post(
        '/map-layers/bridges',
        { geometry, locale: locale.startsWith('de') ? 'de' : 'en' },
        'Brücken konnten nicht geladen werden',
    );

    return {
        categories: payload?.categories ?? [],
        layers: payload?.layers ?? {},
        counts: payload?.counts ?? {},
        total: payload?.total ?? 0,
        truncated: Boolean(payload?.truncated),
    };
};

/**
 * The kinds of bridge offered, in the server's order.
 *
 * `other` is by far the largest — 312.323 of 357.536 bridges are OSM records that
 * say nothing about what crosses them — so it is offered plainly rather than
 * hidden or dressed up as something more specific.
 */
export const BRIDGE_CATEGORIES = ['motorway', 'federalRoad', 'railway', 'other'];

/**
 * The kinds of way offered, in the server's order.
 *
 * Stated here for the same reason as the power categories: the list has to exist
 * before anything is queried. `other` is the catch-all for tags none of the six
 * name — it is offered, but it is also the one that can come back short.
 */
export const WAY_CATEGORIES = ['motorway', 'mainRoad', 'residential', 'service', 'cycle', 'foot', 'other'];

/**
 * The categories offered in the metric list, in the server's order.
 *
 * Stated here because the list has to exist before anything is queried — the
 * reader picks a category first, and only then is there an area to ask about.
 * The colours, the classification and the elements all stay on the server; these
 * are only keys, and a category that fell out of the server's vocabulary would
 * show up as an entry that draws nothing rather than as a wrong picture.
 */
export const POWER_CATEGORIES = [
    'gridInfrastructure',
    'energyGeneration',
    'energyStorage',
    'energyConsumptionTransmission',
    'other',
];

/**
 * Schutzgebiete, Hochwasser (HWRM-RL) und Starkregen — gezeichnet vom Server wie die Chat-Werkzeuge
 * `environment.protected-areas` und `natural-disasters.flood-hazard`.
 */
export type SiteHazardKey =
    | 'protectedAreas'
    | 'floodHazards'
    | 'pluvial'
    | 'seismic'
    | 'climate'
    | 'monuments'
    | 'species'
    | 'zoning'
    | 'pvPotential'
    | 'energyClass';

/** Reihenfolge der Schalter. */
export const SITE_HAZARD_KEYS: SiteHazardKey[] = [
    'protectedAreas',
    'floodHazards',
    'pluvial',
    'seismic',
    'climate',
    'monuments',
    'species',
    'zoning',
    'pvPotential',
    'energyClass',
];

/**
 * Fachliche Gruppen in der Seitenleiste — eine Bauleitplanung ist keine Naturgefahr. Die Erdbeben-Ereignisse
 * stehen in `hazards` vor diesen Schlüsseln.
 */
export type SiteLayerGroup = 'hazards' | 'climate' | 'nature' | 'planning';

export const SITE_LAYER_GROUPS: Record<SiteLayerGroup, SiteHazardKey[]> = {
    hazards: ['seismic', 'floodHazards', 'pluvial'],
    climate: ['climate', 'pvPotential', 'energyClass'],
    nature: ['protectedAreas', 'species'],
    planning: ['zoning', 'monuments'],
};

/**
 * Reihenfolge beim Zeichnen, von unten nach oben: ganze Gemeinden (Artenschutz) und das Klimaraster zuunterst,
 * Denkmäler obenauf — sonst verschwänden die kleinen Flächen unter den großen.
 */
export const SITE_HAZARD_DRAW_ORDER: SiteHazardKey[] = [
    'species',
    'climate',
    'seismic',
    'zoning',
    'pvPotential',
    'energyClass',
    'protectedAreas',
    'floodHazards',
    'pluvial',
    'monuments',
];

export interface SiteHazardResult {
    elements: any[];
    /** Anzahl je Kategorie bzw. Szenario (Schutzgebiete, Hochwasser) */
    counts?: Record<string, number>;
    count?: number;
    truncated?: boolean;
    /** Starkregen und Klima: Kacheln bzw. 1-km-Zellen; Starkregen davon mit Wasser ab 10 cm, davon ohne Daten */
    cells?: number;
    /** Klima: Kennwert, Beschriftung und Zeitraum der Werte */
    label?: string;
    period?: string | null;
    /** Artenschutz: Gemeinden ohne Artnachweis-Import */
    unrecorded?: number;
    wetCells?: number;
    noDataCells?: number;
    clipped?: boolean;
}

const SITE_HAZARD_PATHS: Record<SiteHazardKey, [string, string]> = {
    protectedAreas: ['/map-layers/protected-areas', 'Schutzgebiete konnten nicht geladen werden'],
    floodHazards: ['/map-layers/flood-hazards', 'Hochwasserflächen konnten nicht geladen werden'],
    pluvial: ['/map-layers/pluvial', 'Starkregen konnte nicht geladen werden'],
    seismic: ['/map-layers/seismic', 'Erdbebengefährdung konnte nicht geladen werden'],
    climate: ['/map-layers/climate', 'Klimakennwerte konnten nicht geladen werden'],
    monuments: ['/map-layers/monuments', 'Denkmäler konnten nicht geladen werden'],
    species: ['/map-layers/species', 'Artenschutz konnte nicht geladen werden'],
    zoning: ['/map-layers/zoning', 'Bauleitpläne konnten nicht geladen werden'],
    pvPotential: ['/map-layers/pv-potential', 'PV-Potenzial konnte nicht geladen werden'],
    energyClass: ['/map-layers/energy-class', 'Effizienzklassen konnten nicht geladen werden'],
};

export const loadSiteHazard = async (key: SiteHazardKey, geometry: any): Promise<SiteHazardResult> => {
    const [path, whatFailed] = SITE_HAZARD_PATHS[key];
    const payload = await post(path, { geometry }, whatFailed);
    return { ...payload, elements: payload?.elements ?? [] };
};
